import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';

import { ListService } from 'webcommon/common-api';
import {SystemGuid, TimeFrameDto, TimeFrameTypeDto} from 'webcommon/shared';

import { ApBaseComponent } from '../ap-base-component/index';
import { ApTimeFrameSelectorModalService } from './ap-timeframe-selector-modal.service';

@Component({
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ApTimeFrameComponent),
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ApTimeFrameComponent),
    },
  ],
  selector: 'ap-timeframe',
  templateUrl: './ap-timeframe.component.html',
})
export class ApTimeFrameComponent extends ApBaseComponent implements ControlValueAccessor, Validator, OnChanges, OnInit {
  @Input() disabled: boolean;
  @Input() baseId: string;
  @Input() patientId: string;
  @Input() visitId: string;
  @Input() nonSelectableTimeFrames: string[];
  @Input() timeframe: TimeFrameTypeDto | null;
  @Input() bUsePatient: boolean | null;
  @Input() altDate: TimeFrameTypeDto | null;
  @Input() required: boolean;
  @Input() form?: NgForm;
  @Input() labelName: string;
  @Input() allowFutureDate = true;
  @Input() showOnly = false;
  @Output() timeframeChanged: EventEmitter<TimeFrameTypeDto> = new EventEmitter();
  @Output() timeframeChangeInprogress: EventEmitter<boolean> = new EventEmitter();

  // formControl = new FormControl();
  displayText: string;
  isLoading = false;
  timeFrameDtos: TimeFrameDto[];
  error: string;

  constructor(
    private listService: ListService,
    private systemGuid: SystemGuid,
    private apTimeFrameSelectorModalService: ApTimeFrameSelectorModalService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.timeframe && this.timeframe.Id && this.timeframe.Id !== this.systemGuid.GuidEmpty) {
      this.processDisplayText();
    }

    this.isLoading = true;

    const component = this;

    this.listService.getTimeFrames()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (timeframeDtos) => {
          component.onLoad(timeframeDtos);
        }, () => {
          /* empty */
        }, () => {
          component.isLoading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.timeframe) {
      if (!changes.timeframe.currentValue) {
        this.displayText = '';
      }
      else{
        this.processDisplayText();
      }
    }
  }

  onLoad(timeFrameDtos: TimeFrameDto[]) {
    this.timeFrameDtos = timeFrameDtos;
  }

  openModal() {
    const component = this;
    if (!this.isLoading && !this.showOnly) {
      let timeframeId;
      let timeframeValue;

      if (this.timeframe && this.timeframe.Id !== this.systemGuid.GuidEmpty) {
        timeframeId = this.timeframe.Id;
        timeframeValue = this.timeframe.Value;
      } else {
        timeframeId = null;
        timeframeValue = null;
      }

      this.apTimeFrameSelectorModalService.openModal(this.patientId, this.visitId, this.baseId,
                                                     timeframeId, timeframeValue, this.bUsePatient, this.altDate, this.timeFrameDtos)
        .pipe(takeUntil(component.onDestroy$))
        .subscribe((result) => {
          component.timeframe = result;
          component.timeframeChanged.next(result);
          component.processDisplayText();
          this.onChange(result);
        }, () => {
          //
        });
    }
  }

  processDisplayText() {
    if (this.timeframe && this.timeframe.Id) {
      if (this.timeframe.Id === this.systemGuid.TimeFrame_Calendar) {
        const formattedDate = moment(this.timeframe.Date).format('L');
        this.displayText = formattedDate;
      } else if (this.timeframe.Id === this.systemGuid.TimeFrame_FreeText || this.timeframe.Id === this.systemGuid.TimeFrame_Default) {
        this.displayText = this.timeframe.Value;
      } else if (this.timeframe.Date) {
        const formattedDate = moment(this.timeframe.Date).format('L');

        if (formattedDate === this.timeframe.Answer) {
          this.displayText = formattedDate;
        } else {
          this.displayText = `${this.timeframe.Answer} (${formattedDate})`;
        }
      } else {
          if (this.timeframe.Id === this.systemGuid.TimeFrame_Clear) {
          this.displayText = '';
          } else {
            switch (this.timeframe.Id) {
              case this.systemGuid.TimeFrame_Teenage:
                this.displayText = 'Teenage';
                break;
              case this.systemGuid.TimeFrame_Birth:
                this.displayText = 'Birth';
                break;
              case this.systemGuid.TimeFrame_Childhood:
                this.displayText = 'Childhood';
                break;
              default:
                this.displayText = '';
            }
          }
      }
    } else {
      this.displayText = '';
      this.timeframe = null;
    }
  }

  onChange(value: TimeFrameTypeDto) {
    this.propagateChange(value);
  }

  private propagateChange = (_: any) => {
    /* Will be overridden */
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  writeValue(obj: TimeFrameTypeDto): void {
    this.timeframe = obj;
  }

  registerOnTouched(_: any): void {
    /* For mobile */
  }

  private setError(error: string): ValidationErrors {
    this.error = error;
    return { required: true };
  }

  private clearError(): null {
    this.error = '';
    return null;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value || !control.value.Date) {
      return this.required ? this.setError(this.labelName ? this.labelName + ' is a required field' : 'Required field') : this.clearError();
    } else {
      const date = new Date(control.value.Date);
      if (this.allowFutureDate) return this.clearError();
      if (date > new Date()) {
        return this.setError(
          this.labelName ? this.labelName + ' cannot be in the future' :
          'Date cannot be in the future',
        );
      }
    }

    return this.clearError();
  }
}
